import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The CreatePersonInput used by the constructor of CreatePerson
 */
export interface CreatePersonInput {
  /**
   * first name on the person.
   */
  firstName?: string;
  /**
   * last name on the person.
   */
  lastName?: string;
  /**
   * email on the person.
   */
  email?: string;
  /**
   * phone number on the person, should start with +{country code}
   */
  phone?: string;
  /**
   * any additional metadata attributes required by the person.
   */
  metadata?: {
    [key: string]: string;
  };
  /**
   * Existing person id if updating a customer record
   */
  personId?: string;
}

/**
 * The CreatePerson response expected from the execution of the object within BambuClient execute.
 */
export interface CreatePersonResponse {
  /**
   * the message response from the request
   */
  message: string;
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
  /**
   * If the person already exists, the personId is returned.
   */
  personId?: string;
}

/**
 * The create person api is an asyncronous request to BambuMeta to issue the provided user an person.  This request
 * will not guarentee an person is created.  But checks if there is already an existing person.  If so, the person id is returned.  If no person already exists,
 * the request will be added to the processor for consumption and a success message should be returned.
 */
export class CreatePerson extends Executable<CreatePersonResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the CreatePersonResponse
   * @returns CreatePersonResponse
   */
  public getResponse(apiResponse: any): CreatePersonResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/create-person`;
  }

  private _firstName?: string;

  private _lastName?: string;

  private _email?: string;

  private _phone?: string;

  private _metadata?: any;

  private _personId?: string;

  constructor(input?: CreatePersonInput) {
    super();
    if (input) {
      this._email = input.email;
      this._firstName = input.firstName;
      this._lastName = input.lastName;
      this._phone = input.phone;
      this._metadata = input.metadata;
      this._personId = input.personId;
    }
  }

  /**
   * Sets the first name on the person.
   * @param value <string> first name
   * @returns <CreatePerson>
   */
  public setFirstName(value: string): CreatePerson {
    this._firstName = value;
    return this;
  }

  /**
   * Sets the last name on the person.
   * @param value <string> last name
   * @returns <CreatePerson>
   */
  public setLastName(value: string): CreatePerson {
    this._lastName = value;
    return this;
  }

  /**
   * Sets the email address on the person.
   * @param value <string> email
   * @returns <CreatePerson>
   */
  public setEmail(value: string): CreatePerson {
    this._email = value;
    return this;
  }

  /**
   * Sets the phone number on the person.
   * @param value <string> phone number, should start with +{country code}
   * @returns <CreatePerson>
   */
  public setPhone(value: string): CreatePerson {
    this._phone = value;
    return this;
  }

  /**
   * Sets the personId if known and the customer already exists to update the record.
   * @param value <string> personId if known.
   * @returns <CreatePerson>
   */
  public setPersonId(value: string): CreatePerson {
    this._personId = value;
    return this;
  }

  /**
   * Sets any additional metadata to be stored against the person record
   * @param key the metadata attribute key
   * @param value the metadata attribute value
   * @returns <CreatePerson>
   */
  public setMetaData(key: string, value: string): CreatePerson {
    if (!this._metadata) {
      this._metadata = {};
    }
    this._metadata[key] = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {};
    if (this._email) {
      input.email = this._email;
    }
    if (this._phone) {
      input.phone = this._phone;
    }
    if (this._firstName) {
      input.firstName = this._firstName;
    }
    if (this._lastName) {
      input.lastName = this._lastName;
    }
    if (this._personId) {
      input.personId = this._personId;
    }
    if (this._metadata) {
      input.metadata = this._metadata;
    }
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
